import React from "react";
import { observer } from "owa-mobx-react";
import { navigationPaneLabel } from "./MailModule.locstring.json";
import loc from "owa-localize";
import { getDataMinMaxWidthsForLeftNav, shouldShowFolderPaneAsOverlay, shouldShowFolderPaneAsTransientOverlay, shouldShowFolderPane, getLeftPaneStyles, toggleFolderPaneOnOrientationChange } from "owa-mail-layout";
import { getScreenOrientation } from "owa-layout";
import MailLeftPaneAsOverlay from "./MailLeftPaneAsOverlay";
import FolderPaneContainer from "./FolderPaneContainer";

import { leftPaneContainer, leftPanelSecondRow } from "./MailModule.scss";

import classnames from "owa-classnames";
import { useLeftPaneContentStyle } from "owa-left-pane-layout/lib/utils/useLeftPaneContentStyle";
import type { FunctionComponent } from "react";
import { type StateChangeSource } from "owa-left-pane-layout/lib/types/StateChangeSource";

const defaultExport: FunctionComponent<{}> = observer(function MailLeftPane() {
    const showFolderPane = shouldShowFolderPane();
    const showFolderPaneAsOverlay = shouldShowFolderPaneAsOverlay();
    const showFolderPaneAsTransientOverlay = shouldShowFolderPaneAsTransientOverlay();
    const showFolderPaneInLayout = showFolderPane && !showFolderPaneAsTransientOverlay;
    const leftPaneClasses = classnames(leftPaneContainer, getLeftPaneStyles());
    const { dataMin, dataMax } = getDataMinMaxWidthsForLeftNav();
    
    const memoizedLeftPaneStyle = useLeftPaneContentStyle(showFolderPaneInLayout);
    
    const handleOrientationChange = React.useCallback(() => {
        // On orientation change to portrait mode, if the folder pane is expanded, collapse it to be shown as overlay
        // On orientation change to landscape mode, if the folder pane was overlay switch back to expanded mode if width allows
        // Do not persist the state on server as this is specific to screen orientation that can be differ based on device and screen
        toggleFolderPaneOnOrientationChange(8, getScreenOrientation());
    }, []);
    
    React.useEffect(() => {
        // Compute inside the effect to avoid capturing a stale reference from the render body.
        // In detached/cross-origin contexts (popout windows, WebView2 navigations),
        // window.screen.orientation can become invalid, so we guard with try/catch.
        const screenOrientationMedia: EventTarget | undefined = window.screen?.orientation ?? window.matchMedia?.("(orientation: portrait)");
        
        if (!screenOrientationMedia) {
            return;
        }
        
        try {
            screenOrientationMedia.addEventListener("change", handleOrientationChange);
        }
        catch {
            // ScreenOrientation may throw "Illegal invocation" if the native binding is
            // detached (e.g., closed popout window). Fall through silently.
            return;
        }
        
        return () => {
            try {
                screenOrientationMedia.removeEventListener("change", handleOrientationChange);
            }
            catch {
                // Same guard for cleanup — the target may already be invalid.
            }
        };
    }, [handleOrientationChange]);
    
    return (<div tabIndex={-1} role={"region"} aria-label={loc(navigationPaneLabel)} className={leftPaneClasses} data-min-width={dataMin} data-max-width={dataMax}>
            <div data-app-section="NavigationPane" className={classnames(leftPanelSecondRow, memoizedLeftPaneStyle)}>
                {renderFolderPane(showFolderPaneAsOverlay || showFolderPaneAsTransientOverlay)}
            </div>
        </div>);
}, "MailLeftPane");
export default defaultExport;

/**
 * Renders the folder pane as an overlay or non-overlay
 */
function renderFolderPane(showFolderPaneAsOverlay: boolean) {
    if (!showFolderPaneAsOverlay) {
        return <FolderPaneContainer />;
    }
    return <MailLeftPaneAsOverlay />;
}
